import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { Change } from '@app/models/change';
import { SearchPostQuery } from '@app/models/search-post-query';
import { SearchResult } from '@app/models/search-result';
import { CacheService } from '@app/services/cache.service';
import { SearchService } from '@app/services/search.service';
import { Environment } from '@environments/interface.environment';
import { ListItem } from '@microsoft/microsoft-graph-types';
import { UsedInsight } from '@microsoft/microsoft-graph-types-beta';
import { parseISO } from 'date-fns';
import { concat, merge, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, tap } from 'rxjs/operators';
import { UrlHelperService } from './url-helper.service';

@Injectable({
  providedIn: 'root'
})
export class MyCollaborationActivitiesService {

  constructor(
    private http: HttpClient,
    private cache: CacheService,
    @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
    private urlHelperService: UrlHelperService,
    private searchService: SearchService, private httpHelper: HttpHelper) { }

  private readonly myCollabcacheKey = 'MyCollaborationActivitiesService-getMyFrequentSites';
  private readonly myCollabUserActivityCacheKey = 'MyCollaborationUserActivitiesService-getItemDetails';
  private getItemDetails$ = new Map<string, Observable<ListItem>>();
  private getMyFrequentSites$ = new Map<string, Observable<Array<UsedInsight>>>();
  private getChangesForSite$ = new Map<string, Observable<Array<Change>>>();
  private siteExclude = '/News';
  public useCache = false;

  getMyFrequentSites(amount: number = 10): Observable<Array<UsedInsight>> {
    const key = `${this.myCollabcacheKey}-${amount}`;
    if (this.getMyFrequentSites$.has(key)) {
      return this.getMyFrequentSites$.get(key);
    } else {
      const httpParamsMyCollabActivities = new HttpParams()
        .append('$filter', 'resourceVisualization/type eq \'spsite\'')
        .append('$orderBy', 'lastUsed/lastAccessedDateTime desc')
        .append('$top', (amount + 1).toString());

      // Graph API call to fetch user activities
      this.getMyFrequentSites$.set(key, this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_BETA_VERSION}/me/insights/used`,
        {
          params: httpParamsMyCollabActivities
        })
        .pipe(
          map(result => {
            console.log('Collab Frequent Sites:' + result.value);
            const filteredResults = result.value.filter(frequentSite => frequentSite.resourceReference.webUrl.indexOf(this.siteExclude) === -1) as Array<UsedInsight>;
            if (filteredResults.length > amount) {
              return filteredResults.slice(0, amount);
            }
            return filteredResults;
          }),
          tap(data => {
            this.useCache && this.cache.set(key, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getMyFrequentSites', result: new Array<UsedInsight>(), cacheKey: key })),
          shareReplay({ bufferSize: 1, refCount: true })));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<UsedInsight>>(key);
        if (cachedData) {
          return concat(of(cachedData), this.getMyFrequentSites$.get(key));
        }
      }
      return this.getMyFrequentSites$.get(key);
    }

  }

  getChangesForSite(resource: UsedInsight, maxAmountOfChanges: number = 2): Observable<Array<Change>> {
    const serviceMapKey = `${resource.resourceReference.webUrl}-${maxAmountOfChanges}`;
    if (this.getChangesForSite$.has(serviceMapKey)) {
      return this.getChangesForSite$.get(serviceMapKey);
    } else {
      const changeQuery = {
        'query':
        {
          'View': true,
          'LatestFirst': true,
          'Item': true,
          'Add': true,
          'DeleteObject': true,
          'Rename': true,
          'Update': true,
          'FetchLimit': '200',
          'RequireSecurityTrim': true
        }
      };
      this.getChangesForSite$.set(serviceMapKey, this.http.post<any>(`${resource.resourceReference.webUrl}/_api/web/getchanges`, changeQuery)
        .pipe(
          mergeMap(changesResult => {
            const changes = new Map<string, Change>();
            const queryText = new Array<string>();
            (changesResult.value as Array<Change>).forEach(change => {
              if (changes.size <= maxAmountOfChanges) {
                const key = `${resource.resourceReference.webUrl}-${change.ItemId}-${change.ListId}-${change.SiteId}-${change.ChangeType.toString()}`;
                if (!changes.has(key)) {
                  change.Title = change.ItemId.toString();
                  change.SiteTitle = resource.resourceVisualization.title;
                  change.SiteUrl = resource.resourceReference.webUrl;
                  change.TimeAsDate = parseISO(change.Time);
                  change.ItemUrl = `${resource.resourceReference.webUrl}/_layouts/15/shortcutlink.aspx?siteId=${change.SiteId}&webId=${change.WebId}&uniqueId=${change.UniqueId}`;
                  queryText.push(`uniqueid:{${change.UniqueId}}`);
                  changes.set(key, change);
                }
              }
            });

            if (queryText.length > 0) {
              const queryTextWithOr = queryText.join(' OR ');
              let searchRequests: Array<Observable<SearchResult[]>>;
              if (queryTextWithOr.length >= 4096) { // Query text only supports max 4096 characters so we need to split the queries in that case
                const amountOfSplits = Math.ceil(queryTextWithOr.length / 4096);
                const splittedQueryText = this.chunk(queryText, queryText.length / amountOfSplits);
                searchRequests = splittedQueryText.map(value => {
                  const searchRequest = this.buildSearchPostQuery(value);
                  return this.searchService.sendPostSearchQuery(searchRequest);
                });
              } else {
                const searchRequest = this.buildSearchPostQuery(queryText);
                searchRequests = [this.searchService.sendPostSearchQuery(searchRequest)];
              }
              return merge(...searchRequests)
                .pipe(
                  map((searchResults) => {
                    changes.forEach((change) => {
                      const result = searchResults.find(s => s.UniqueId.toLowerCase() === `{${change.UniqueId.toLowerCase()}}`);
                      if (result) {
                        change.Title = result.Title || result.Filename;
                        change.ItemUrl = result.Path;
                      } else {
                        change.ListItem$ = this.getItemDetails(change.SiteId, change.ListId, change.ItemId);
                      }
                    });
                    return [...changes.values()];
                  }));
            } else {
              return of([...changes.values()]);
            }
          }),
          shareReplay({ bufferSize: 1, refCount: true })));
      return this.getChangesForSite$.get(serviceMapKey);
    }
  }

  getItemDetails(siteId: string, listId: string, itemId: number): Observable<ListItem> {
    const key = `${this.myCollabUserActivityCacheKey}-${siteId}-${listId}-${itemId}`;
    if (this.getItemDetails$.has(key)) {
      return this.getItemDetails$.get(key);
    } else {

      const httpParams = new HttpParams()
        .append('expand', 'fields(select=FileLeafRef)')
        .append('select', 'fields,id,webUrl');

      siteId = siteId.replace(/-/g, '');
      listId = listId.replace(/-/g, '');

      this.getItemDetails$.set(key, this.http.get<ListItem>(
        `${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION_ONE}/sites/${siteId}/lists/${listId}/items/${itemId}`,
        { params: httpParams })
        .pipe(
          map(data => {
            if (data.webUrl.indexOf('?') === -1) {
              data.webUrl = `${data.webUrl}?Web=1`;
            }
            return data;
          }),
          tap(data => {
            this.useCache && this.cache.set(key, data);
          }),
          shareReplay({ bufferSize: 1, refCount: true })));

      if (this.useCache) {
        const cachedData = this.cache.get<ListItem>(key);
        if (cachedData) {
          return concat(of(cachedData), this.getItemDetails$.get(key));
        }
      }
      return this.getItemDetails$.get(key);
    }
  }

  private chunk<T>(arr: Array<T>, numOfParts: number) {
    const temparray = new Array<Array<T>>();
    for (let i = 0; i < arr.length; i += numOfParts) {
      temparray.push(arr.slice(i, i + numOfParts));
    }
    return temparray;
  }

  private buildSearchPostQuery(value: string[]) {
    return {
      request: {
        Querytext: value.join(' OR '),
        RowLimit: 100,
        SelectProperties: ['Title', 'Filename', 'UniqueId', 'Path'],
        TrimDuplicates: false,
        Properties: [
          {
            Name: 'EnableDynamicGroups',
            Value: {
              BoolVal: 'True',
              QueryPropertyValueTypeIndex: 3
            }
          }
        ]
      }
    } as SearchPostQuery;
  }
}
